import {useMutation} from '@apollo/client'
import {FormEvent, useEffect, useState} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {EDIT_POLICY} from '../../../gql/mutations/policyMutation'
import {IResUpdatePolicyApi} from '../../../types/policies'
import {parseId, hasPermission} from '../../../helpers'
import Select from 'react-select'
import {getBootstrapColorHex, ALL_PERMISSIONS, STATUS_POLICY} from '../../../helpers/consts'
import {useGetUser} from '../../../store/userStore'
import {DateInput} from 'app/tfi/components/DateInput'
import moment from 'moment'

/**
 * Llaves NUEVAS consumidas vía el helper `t()` (el extractor no sigue wrappers, guía §17.12).
 * Las opciones de estado de documentos/pago se REUSAN de `policies.list.filters.{ds,ps}.*`.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const M = defineMessages({
  title: {id: 'policies.detail.statuses.title', defaultMessage: 'Policy Statuses'},
  toastUpdated: {
    id: 'policies.detail.statuses.toast.updated',
    defaultMessage: 'Status updated successfully',
  },
  noPermDocs: {
    id: 'policies.detail.statuses.noPermDocs',
    defaultMessage: "You don't have permissions to modify document status",
  },
  noPermPayment: {
    id: 'policies.detail.statuses.noPermPayment',
    defaultMessage: "You don't have permissions to modify payment status",
  },
  chargeback: {id: 'policies.detail.statuses.ps.chargeback', defaultMessage: 'Chargeback'},
  canceledDate: {
    id: 'policies.detail.statuses.canceledDate',
    defaultMessage: 'Cancellation date',
  },
  canceledDateRequired: {
    id: 'policies.detail.statuses.canceledDateRequired',
    defaultMessage: 'The cancellation date is required to set a canceled status',
  },
  canceledDateHint: {
    id: 'policies.detail.statuses.canceledDateHint',
    defaultMessage: 'Date the coverage actually ends. Defaults to the end of the current month.',
  },
})

interface IModalProps {
  id: string
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  statusPolicy: any
  loadingPoliciesStatus
  currentStatus?: string
  currentDocumentStatus?: string
  currentPaymentStatus?: string
  currentDocumentsDueDate?: string | null
  currentCanceledDate?: string | null
  isQuotePolicy?: boolean
}

export const ModalStatusesPolicy: React.FC<IModalProps> = ({
  id,
  isOpen,
  onClose,
  refetch,
  statusPolicy,
  loadingPoliciesStatus,
  currentStatus = '',
  currentDocumentStatus = '',
  currentPaymentStatus = '',
  currentDocumentsDueDate = '',
  currentCanceledDate = '',
  isQuotePolicy = false,
}) => {
  const intl = useIntl()
  const t = (id: string, defaultMessage: string) => intl.formatMessage({id, defaultMessage})
  const selectOne = t('common.placeholder.selectOption', 'Select an option')
  // Obtener el usuario actual, sus grupos y permisos
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const permissions = useGetUser((s) => s.user?.permissions)
  const isAsesor = userGroups.includes('Asesor')
  const canMarkAsCompleted = permissions && hasPermission(permissions, ALL_PERMISSIONS.MARK_COMPLETED_POLICY)

  // Si tiene el permiso MARK_COMPLETED_POLICY, puede editar todo sin restricciones
  const hasRestrictedAccess = isAsesor && !canMarkAsCompleted


  // const {
  //   data: dataPoliciesStatuses,
  //   loading: loadingPoliciesStatus,
  //   error: errorPoliciesStatuses,
  // } = useQuery<AllPolicyStatusesData, AllPolicyStatusesVars>(GET_ALL_POLICIES_STATUSES, {
  //   fetchPolicy: 'cache-first',
  // })

  // Filtrar los status según el rol del usuario
  const allStatusOptions =
    statusPolicy?.allPolicyStatuses.edges.map(({node}) => ({
      value: node.code,
      label: node.title,
      category: node.category,
      // El backend decide quién puede asignar cada estado (core.authz.policy_rules).
      assignable: node.assignable !== false,
      color: getBootstrapColorHex(node.color || 'gray'),
      icon: <i className='bi bi-circle-fill' style={{color: getBootstrapColorHex(node.color || 'gray')}}></i>,
    })) || []

  // El selector muestra solo lo que el usuario puede asignar. La regla ya no vive acá.
  const statusOptions = allStatusOptions.filter((option) => option.assignable)

  const customSingleValue = ({data}: any) => (
    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
      {data.icon}
      {data.label}
    </div>
  )

  const customOption = (props: any) => {
    const {data, innerRef, innerProps} = props
    return (
      <div
        ref={innerRef}
        {...innerProps}
        style={{display: 'flex', alignItems: 'center', padding: 10}}
      >
        {data.icon}
        <span style={{marginLeft: 8}}>{data.label}</span>
      </div>
    )
  }

  const [formData, setFormData] = useState({
    statusPolicy: '',
    documentStatus: '',
    documentsDueDate: '',
    paymentStatus: '',
    canceledDate: '',
  })

  const [gqlCreateP, {data: dataUpdate, loading: loadingUpdate}] =
    useMutation<IResUpdatePolicyApi>(EDIT_POLICY)

  const selectedStatusCategory =
    allStatusOptions.find((option) => option.value === formData.statusPolicy)?.category || null
  // La fecha de cancelacion es un dato de negocio (cuando termina la cobertura), no el
  // momento del clic: por eso se pregunta aca en vez de sellarse sola. Ver
  // core.services.policy_status.ensure_status_timestamps, que respeta la que llega.
  const isCanceledStatus = selectedStatusCategory === STATUS_POLICY.CANCELED
  // No puede existir una poliza cancelada sin fecha de cancelacion: es el dato con el que
  // se calcula el tiempo activo, y sellarla con el dia del clic falsea la cobertura. Mismo
  // criterio que la fecha limite de documentos.
  const shouldRequireCanceledDate = isCanceledStatus && !hasRestrictedAccess
  const shouldShowDocumentsDueDate =
    formData.documentStatus === 'PENDING' || formData.documentStatus === 'PROCESSING'
  const shouldRequireDocumentsDueDate =
    !hasRestrictedAccess &&
    formData.documentStatus === 'PENDING' &&
    selectedStatusCategory === STATUS_POLICY.COMPLETED

  useEffect(() => {
    if (!dataUpdate) return

    if (dataUpdate && dataUpdate.updatePolicy.success) {
      toast.success(t('policies.detail.statuses.toast.updated', 'Status updated successfully'))
      refetch()
      onClose()
    } else if (dataUpdate.updatePolicy.errors && dataUpdate.updatePolicy.errors.length > 0) {
      toast.error(
        intl.formatMessage(
          {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
          {message: dataUpdate.updatePolicy.errors[0].message}
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdate])

  const handleDateChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (shouldRequireCanceledDate && !formData.canceledDate) {
      toast.error(intl.formatMessage(M.canceledDateRequired))
      return
    }

    if (shouldRequireDocumentsDueDate && !formData.documentsDueDate) {
      toast.error(
        t(
          'policies.list.quickUpdate.toast.docsDueRequired',
          'Documents due date is required when document status is Pending and policy status is Completed'
        )
      )
      return
    }

    const policyData: Record<string, any> = {
      ...(!isQuotePolicy && formData.statusPolicy ? {status: formData.statusPolicy} : {}),
      // Obligatoria al cancelar (ver la guarda de arriba). El backend la respeta tal cual
      // en vez de sellar la fecha de hoy.
      ...(!isQuotePolicy && isCanceledStatus && !hasRestrictedAccess && formData.canceledDate
        ? {canceledDate: formData.canceledDate}
        : {}),
      ...(formData.documentStatus ? {stateDocs: formData.documentStatus} : {}),
      ...(formData.paymentStatus ? {paymentStatus: formData.paymentStatus} : {}),
    }

    if (!hasRestrictedAccess && formData.documentStatus) {
      policyData.documentsDueDate =
        formData.documentStatus === 'PENDING' || formData.documentStatus === 'PROCESSING'
          ? formData.documentsDueDate || null
          : null
    }

    gqlCreateP({
      variables: {
        id: parseId(id),
        policyData,
      },
    })
  }

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        statusPolicy: '',
        documentStatus: '',
        documentsDueDate: '',
        paymentStatus: '',
        canceledDate: '',
      })
      return
    }

    setFormData({
      statusPolicy: currentStatus || '',
      documentStatus: currentDocumentStatus || '',
      documentsDueDate: currentDocumentsDueDate || '',
      paymentStatus: currentPaymentStatus || '',
      canceledDate: currentCanceledDate || '',
    })
  }, [
    isOpen,
    currentStatus,
    currentDocumentStatus,
    currentDocumentsDueDate,
    currentPaymentStatus,
    currentCanceledDate,
  ])

  return (
    <Modal show={isOpen} onHide={onClose} className='modal-statuss-policy'>
      <Modal.Header closeButton>
        <Modal.Title>
          <FormattedMessage id='policies.detail.statuses.title' defaultMessage='Policy Statuses' />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form id='formId' onSubmit={handleSubmit}>
          {!isQuotePolicy && (
            <Form.Group>
              <Form.Label>
                <FormattedMessage id='common.field.status' defaultMessage='Status' />
              </Form.Label>
              <Select
                className='selectable'
                options={statusOptions}
                value={statusOptions.find((opt) => opt.value === formData.statusPolicy)}
                onChange={(selected) => {
                  const nextCategory = allStatusOptions.find(
                    (opt) => opt.value === selected?.value
                  )?.category
                  setFormData((prev) => ({
                    ...prev,
                    statusPolicy: selected?.value || '',
                    // Sugerencia, no imposicion: el usuario la puede cambiar.
                    canceledDate:
                      nextCategory === STATUS_POLICY.CANCELED && !prev.canceledDate
                        ? moment().endOf('month').format('YYYY-MM-DD')
                        : prev.canceledDate,
                  }))
                }}
                isSearchable={true}
                filterOption={(option, inputValue) =>
                  option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                  option.value.toLowerCase().includes(inputValue.toLowerCase())
                }
                components={{SingleValue: customSingleValue, Option: customOption}}
                styles={{
                  control: (base) => ({...base, minHeight: 40}),
                  option: (base, {data, isFocused}) => ({
                    ...base,
                    backgroundColor: isFocused ? '#f0f0f0' : 'white',
                    color: data.color,
                  }),
                }}
                placeholder={selectOne}
                isDisabled={loadingPoliciesStatus}
              />
            </Form.Group>
          )}
          {!isQuotePolicy && isCanceledStatus && (
            <Form.Group className='mt-2'>
              <DateInput
                id='canceledDate'
                name='canceledDate'
                label={intl.formatMessage(M.canceledDate)}
                value={formData.canceledDate}
                onChange={handleDateChange}
                disabled={hasRestrictedAccess}
                required={shouldRequireCanceledDate}
              />
              <Form.Text className='text-muted'>{intl.formatMessage(M.canceledDateHint)}</Form.Text>
            </Form.Group>
          )}
          <Form.Group className='mt-2'>
            <Form.Label>
              <FormattedMessage
                id='policies.list.quickUpdate.field.docStatus'
                defaultMessage='Documents status'
              />
            </Form.Label>
            <Form.Select
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  documentStatus: e.target.value,
                  documentsDueDate: e.target.value === 'PENDING' || e.target.value === 'PROCESSING' ? prev.documentsDueDate : '',
                }))
              }
              value={formData.documentStatus ?? ''}
              disabled={hasRestrictedAccess}
            >
              <option value=''>{selectOne}</option>
              <option value='ACTIVE'>{t('policies.list.filters.ds.active', 'Active')}</option>
              <option value='PENDING'>{t('policies.list.filters.ds.pending', 'Pending')}</option>
              <option value='PROCESSING'>{t('policies.list.filters.ds.processing', 'Processing')}</option>
              <option value='DECLINED'>{t('policies.list.filters.ds.declined', 'Declined')}</option>
              <option value='COMPLETED'>{t('policies.list.filters.ds.completed', 'Completed')}</option>
            </Form.Select>
            {hasRestrictedAccess && (
              <Form.Text className='text-muted'>
                <FormattedMessage
                  id='policies.detail.statuses.noPermDocs'
                  defaultMessage="You don't have permissions to modify document status"
                />
              </Form.Text>
            )}
          </Form.Group>
          {shouldShowDocumentsDueDate && (
            <Form.Group className='mt-2'>
              <DateInput
                id='documentsDueDate'
                name='documentsDueDate'
                label={t('policies.list.quickUpdate.docsDueDate', 'Documents Due Date')}
                value={formData.documentsDueDate}
                onChange={handleDateChange}
                disabled={hasRestrictedAccess}
                required={shouldRequireDocumentsDueDate}
              />
            </Form.Group>
          )}
          <Form.Group className='mt-2'>
            <Form.Label>
              <FormattedMessage
                id='policies.list.filters.field.paymentStatus'
                defaultMessage='Payment status'
              />
            </Form.Label>
            <Form.Select
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  paymentStatus: e.target.value,
                }))
              }
              value={formData.paymentStatus ?? ''}
              disabled={hasRestrictedAccess}
            >
              <option value=''>{selectOne}</option>
              <option value='PAID'>{t('policies.list.filters.ps.paid', 'Paid')}</option>
              <option value='FAILED'>{t('policies.list.filters.ps.failed', 'Failed')}</option>
              <option value='PENDING'>{t('policies.list.filters.ps.pending', 'Pending')}</option>
              <option value='AUTO_PAY'>{t('policies.list.filters.ps.autoPay', 'Auto Pay')}</option>
              <option value='NOT_APPLICABLE'>
                {t('policies.list.filters.ps.notApplicable', 'Not Applicable ($0)')}
              </option>
              <option value='PAGA_POR_SU_CUENTA'>
                {t('policies.list.filters.ps.pagaPorSuCuenta', 'Paga por su cuenta')}
              </option>
              <option value='CHARGEBACK'>
                {t('policies.detail.statuses.ps.chargeback', 'Chargeback')}
              </option>
            </Form.Select>
            {hasRestrictedAccess && (
              <Form.Text className='text-muted'>
                <FormattedMessage
                  id='policies.detail.statuses.noPermPayment'
                  defaultMessage="You don't have permissions to modify payment status"
                />
              </Form.Text>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
        </Button>
        <Button
          variant='primary'
          type='submit'
          form='formId'
          formAction='formId'
          disabled={loadingUpdate}
        >
          {loadingUpdate && <Spinner animation='border' size='sm' className='me-2' />} <FormattedMessage id='common.action.saveChanges' defaultMessage='Save changes' />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
